/**
 * RouteToReviewersButton.jsx — loop-close v1 (extracted RC 1.62).
 *
 * Originally inlined in `EmployerRoleMatchesPage.js` (legacy matches
 * page). RC 1.62 Channel-2 Lifecycle Completion extracts it so the
 * Surfaced Candidates panel can render the SAME affordance on
 * accepted-revealed rows without duplicating logic.
 *
 * RC 1.62 M146 (2026-05-30) — extended to also support routing
 * Channel-1 Imported Candidates. Caller passes EITHER:
 *   • `candidateId`     — NDOR-Surfaced revealed candidate
 *                          (profiles.id; existing behaviour)
 *   • `empCandidateId`  — imported emp_candidates.id
 * Exactly one must be set (xor mirrors backend M146 contract).
 *
 * Action:
 *   POST /api/employer/v1/reviewer/reviews/route
 *   { account_id, role_id, candidate_user_id | emp_candidate_id }
 *   Idempotency-Key header is set per click.
 *
 * Pre-check (advisory only, NOT a guarantee):
 *   one Supabase JS read of emp_candidate_reviews filtered by the
 *   appropriate axis. If rows exist → label switches to
 *   "Already routed". Race conditions are accepted; if the read
 *   fails we fall back to the default label.
 */
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Loader2, UserCheck } from 'lucide-react';
import { toast } from 'sonner';

import { routeCandidate } from '@/lib/employerReviews';
import {
  listExistingReviewsForRoleCandidate,
  listExistingReviewsForRoleEmpCandidate,
} from '@/lib/employerReviewerRoster';
import { t, tn } from '@/lib/employerReviewerStrings';
export default function RouteToReviewersButton({
  matchId,
  candidateId,
  empCandidateId,
  accountId,
  roleId,
  activeReviewerCount,
  onRouted,
}) {
  const [busy,    setBusy]    = useState(false);
  const [routed,  setRouted]  = useState(false);
  const [already, setAlready] = useState(false);

  // RC 1.62 M146 — kind discriminator. The two paths share the
  // same UI affordance but route via different identifiers.
  const targetKind = empCandidateId ? 'emp_candidate' : 'profile';

  // Best-effort advisory pre-check.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!roleId) return;
      try {
        const rows = targetKind === 'emp_candidate'
          ? await listExistingReviewsForRoleEmpCandidate(roleId, empCandidateId)
          : await listExistingReviewsForRoleCandidate(roleId, candidateId);
        if (!cancelled && rows && rows.length > 0) setAlready(true);
      } catch {
        /* advisory — ignore */
      }
    })();
    return () => { cancelled = true; };
  }, [roleId, candidateId, empCandidateId, targetKind]);

  const noReviewers = !activeReviewerCount || activeReviewerCount <= 0;
  const noTarget    = !candidateId && !empCandidateId;
  const disabled    = busy || noReviewers || !accountId || noTarget;

  const label = routed
    ? t('route.action.routed')
    : already
      ? t('route.action.already_routed')
      : t('route.action.default');

  const title = noReviewers ? t('route.action.disabled.no_reviewers') : undefined;

  const genKey = () =>
    `route-${matchId || candidateId || empCandidateId}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

  const onClick = async () => {
    if (disabled) return;
    setBusy(true);
    try {
      const resp = await routeCandidate({
        accountId,
        roleId,
        candidateUserId: targetKind === 'profile'       ? candidateId    : null,
        empCandidateId:  targetKind === 'emp_candidate' ? empCandidateId : null,
        idempotencyKey:  genKey(),
      });
      const n = Number(resp?.reviewers_routed ?? 0);
      // RC 1.62 Reviewer Dashboard CTA — surface a follow-up nav
      // affordance on the success toast so owner/recruiter can step
      // straight into the reviewer roster (where they assign, pause,
      // and observe coverage). Doctrine: the routing actor never
      // becomes a reviewer; the CTA lands on the ADMIN surface
      // `/employer/reviewers`, not the reviewer's own queue.
      toast.success(tn('route.toast.success', { n }), {
        action: {
          label: t('route.toast.cta.view_reviewers'),
          onClick: () => { window.location.href = '/employer/reviewers'; },
        },
      });
      setRouted(true);
      setAlready(true);
      if (typeof onRouted === 'function') onRouted();
    } catch (e) {
      toast.error(e?.message || t('route.toast.fail'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button
      size="sm"
      variant="outline"
      disabled={disabled}
      onClick={onClick}
      title={title}
      aria-label={t('route.aria.routed_match')}
      data-testid={`match-route-${matchId || empCandidateId || candidateId}`}
      data-target-kind={targetKind}
    >
      {busy
        ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
        : <UserCheck className="h-4 w-4 mr-1.5" />}
      {label}
    </Button>
  );
}
